import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import ReactDOM from 'react-dom'; // Add this import

const ColumnChooser = ({ columns, onChange, onReset }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate dropdown position when opening
  const toggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.right - 260 + window.scrollX // 260 is dropdown width
      });
    }
    setIsOpen(!isOpen);
  };

  const toggleColumn = (id) => {
    const updated = columns.map(col =>
      col.id === id ? { ...col, visible: !col.visible } : col
    );
    onChange(updated);
  };

  const toggleAllColumns = (visible) => {
    const updated = columns.map(col => ({ ...col, visible }));
    onChange(updated);
  };

  const visibleCount = columns.filter(col => col.visible).length;
  const totalCount = columns.length;

  // Dropdown content
  const dropdownContent = isOpen && (
    <div 
      ref={dropdownRef}
      style={{ 
        position: 'fixed',
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: '260px',
        zIndex: 9999,
        maxHeight: 'min(400px, 80vh)',
        overflowY: 'auto',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        border: '1px solid rgba(0,0,0,0.1)'
      }}
    >
      {/* Dropdown Header */}
      <div className="p-3 border-bottom" style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
        <div className="d-flex justify-content-between align-items-center">
          <span className="text-sm fw-semibold">Show Columns</span>
          <div className="d-flex gap-1">
            <button
              className="btn btn-link btn-sm p-0 text-decoration-none"
              onClick={() => toggleAllColumns(true)}
            >
              Show All
            </button>
            <span className="text-muted">|</span>
            <button
              className="btn btn-link btn-sm p-0 text-decoration-none"
              onClick={() => toggleAllColumns(false)}
            >
              Hide All
            </button>
          </div>
        </div>
      </div>

      {/* Column List */}
      <div className="p-2">
        {columns.map((column) => (
          <div
            key={column.id}
            className="d-flex align-items-center justify-content-between px-3 py-2 rounded-2"
            style={{ cursor: 'pointer' }}
            onClick={() => toggleColumn(column.id)}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span className="text-sm text-gray-700">{column.label}</span>
            {column.visible ? (
              <Icon 
                icon="ion:checkmark-circle" 
                className="text-primary" 
                style={{ fontSize: '20px' }}
              />
            ) : (
              <div 
                className="border rounded-circle"
                style={{ width: '20px', height: '20px' }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Dropdown Footer with Reset */}
      {onReset && (
        <div className="p-2 border-top" style={{ position: 'sticky', bottom: 0, background: 'white' }}>
          <button
            className="btn btn-link btn-sm w-100 text-decoration-none text-muted"
            onClick={() => {
              onReset();
              setIsOpen(false);
            }}
          >
            <Icon icon="ion:refresh-outline" className="me-1" />
            Reset to Default
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Button */}
      <button
        ref={buttonRef}
        className="btn btn-outline-secondary h-40-px d-flex align-items-center gap-2"
        onClick={toggleDropdown}
      >
        <Icon icon="ion:grid-outline" className="icon" />
        Columns
        <span className="badge bg-secondary text-white rounded-pill ms-1">
          {visibleCount}/{totalCount}
        </span>
      </button>

      {/* Render dropdown in portal */}
      {ReactDOM.createPortal(dropdownContent, document.body)}
    </>
  );
};

export default ColumnChooser;